import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Users,
    Search,
    Filter,
    Mail,
    Phone,
    Calendar,
    MapPin,
    Briefcase,
    Star,
    MessageSquare,
    Eye,
    ChevronRight,
    Shield,
    Target,
    TrendingUp,
    Users2,
    MoreVertical,
    UserPlus,
    Download
} from "lucide-react";
import BackButton from "../../components/BackButton";
import teamImg from "../../imgs/teams.svg";

const ViewTeams = () => {
    const [teams, setTeams] = useState([
        {
            id: 1,
            name: "Design Team",
            description: "Responsible for UI/UX design and user experience",
            manager: "Alex Johnson",
            members: 8,
            projects: 3,
            location: "San Francisco, CA",
            created: "2023-01-15",
            status: "active",
            skills: ["UI/UX", "Figma", "Prototyping", "Illustration"]
        },
        {
            id: 2,
            name: "Development Team",
            description: "Frontend and backend development of web applications",
            manager: "Maria Garcia",
            members: 12,
            projects: 5,
            location: "Remote",
            created: "2022-11-08",
            status: "active",
            skills: ["React", "Node.js", "TypeScript", "MongoDB"]
        },
        {
            id: 3,
            name: "Quality Assurance",
            description: "Testing and quality assurance for all products",
            manager: "Sarah Wilson",
            members: 6,
            projects: 4,
            location: "Austin, TX",
            created: "2023-03-22",
            status: "active",
            skills: ["Testing", "Automation", "Cypress", "Jest"]
        },
        {
            id: 4,
            name: "DevOps Team",
            description: "Infrastructure and deployment operations",
            manager: "James Miller",
            members: 5,
            projects: 2,
            location: "New York, NY",
            created: "2023-02-10",
            status: "active",
            skills: ["Docker", "AWS", "Kubernetes", "CI/CD"]
        },
        {
            id: 5,
            name: "Data Analytics",
            description: "Data analysis and business intelligence",
            manager: "Lisa Chen",
            members: 7,
            projects: 3,
            location: "Boston, MA",
            created: "2022-12-05",
            status: "active",
            skills: ["Python", "SQL", "Tableau", "Machine Learning"]
        },
        {
            id: 6,
            name: "Product Management",
            description: "Product strategy and roadmap planning",
            manager: "David Kim",
            members: 4,
            projects: 6,
            location: "Chicago, IL",
            created: "2023-04-18",
            status: "planning",
            skills: ["Agile", "Scrum", "Product Strategy", "Market Research"]
        }
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("all");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [expandedTeam, setExpandedTeam] = useState(null);

    const locations = ["all", "San Francisco, CA", "Remote", "Austin, TX", "New York, NY", "Boston, MA", "Chicago, IL"];
    const statusOptions = ["all", "active", "planning", "inactive"];

    const teamMembers = [
        { teamId: 1, members: [
                { name: "Alex Johnson", role: "Lead Designer", avatar: "AJ" },
                { name: "Emma Davis", role: "UI Designer", avatar: "ED" },
                { name: "Michael Brown", role: "UX Researcher", avatar: "MB" }
            ]},
        { teamId: 2, members: [
                { name: "Maria Garcia", role: "Senior Developer", avatar: "MG" },
                { name: "Robert Wilson", role: "Frontend Dev", avatar: "RW" },
                { name: "Jennifer Lee", role: "Backend Dev", avatar: "JL" }
            ]}
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case "active": return "bg-green-100 text-green-800";
            case "planning": return "bg-yellow-100 text-yellow-800";
            case "inactive": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getTeamMembers = (teamId) => {
        const team = teamMembers.find(t => t.teamId === teamId);
        return team ? team.members : [];
    };

    const filteredTeams = teams.filter(team => {
        const matchesSearch =
            team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            team.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            team.manager.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesLocation = selectedLocation === "all" || team.location === selectedLocation;
        const matchesStatus = selectedStatus === "all" || team.status === selectedStatus;

        return matchesSearch && matchesLocation && matchesStatus;
    });

    const stats = {
        totalTeams: teams.length,
        totalMembers: teams.reduce((sum, team) => sum + team.members, 0),
        activeTeams: teams.filter(t => t.status === "active").length,
        totalProjects: teams.reduce((sum, team) => sum + team.projects, 0),
        avgTeamSize: Math.round(teams.reduce((sum, team) => sum + team.members, 0) / teams.length)
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <BackButton to="/team-member/dashboard" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Team Directory</h1>
                                <p className="text-gray-600 mt-1">Browse and connect with teams across the organization</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button className="flex items-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                <Download className="w-4 h-4 mr-2" />
                                Export
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-8">
                {/* Stats Overview */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Teams</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.totalTeams}</p>
                                </div>
                                <Users2 className="w-8 h-8 text-primary-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Team Members</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.totalMembers}</p>
                                </div>
                                <Users className="w-8 h-8 text-blue-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Active Teams</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.activeTeams}</p>
                                </div>
                                <TrendingUp className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Projects</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.totalProjects}</p>
                                </div>
                                <Target className="w-8 h-8 text-purple-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Avg. Team Size</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.avgTeamSize}</p>
                                </div>
                                <Star className="w-8 h-8 text-yellow-600" />
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Search and Filters */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search teams..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>
                        <div className="flex gap-3">
                            <select
                                value={selectedLocation}
                                onChange={(e) => setSelectedLocation(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {locations.map(location => (
                                    <option key={location} value={location}>
                                        {location === "all" ? "All Locations" : location}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {statusOptions.map(status => (
                                    <option key={status} value={status}>
                                        {status === "all" ? "All Status" : status}
                                    </option>
                                ))}
                            </select>
                            <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                                <Filter className="w-5 h-5 mr-2" />
                                More Filters
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Teams List */}
                    <div className="lg:col-span-2">
                        <div className="space-y-4">
                            {filteredTeams.map((team, index) => (
                                <motion.div
                                    key={team.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="card hover:shadow-medium group"
                                >
                                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-start space-x-3">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                                                        <Users className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center space-x-3 mb-1">
                                                            <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
                                                            <span className={`badge ${getStatusColor(team.status)}`}>
                                {team.status}
                              </span>
                                                        </div>
                                                        <p className="text-gray-600 text-sm mb-3">{team.description}</p>

                                                        <div className="flex flex-wrap gap-4 text-sm mb-3">
                                                            <div className="flex items-center space-x-1">
                                                                <Briefcase className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">
                                  <span className="font-medium">{team.members}</span> members
                                </span>
                                                            </div>
                                                            <div className="flex items-center space-x-1">
                                                                <Target className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">
                                  <span className="font-medium">{team.projects}</span> projects
                                </span>
                                                            </div>
                                                            <div className="flex items-center space-x-1">
                                                                <MapPin className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{team.location}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-1">
                                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">Since {team.created}</span>
                                                            </div>
                                                        </div>

                                                        {/* Skills */}
                                                        <div className="flex flex-wrap gap-2 mb-4">
                                                            {team.skills.map((skill, idx) => (
                                                                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                  {skill}
                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Team Members */}
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <Shield className="w-4 h-4 text-gray-400" />
                                                        <span className="text-sm text-gray-600">Team Manager:</span>
                                                        <span className="text-sm font-medium text-gray-900">{team.manager}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
                                                        className="flex items-center text-primary-600 hover:text-primary-700 font-medium"
                                                    >
                                                        {expandedTeam === team.id ? 'Hide Members' : 'View Members'}
                                                        <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${
                                                            expandedTeam === team.id ? 'rotate-90' : ''
                                                        }`} />
                                                    </button>
                                                </div>

                                                {/* Expanded Members View */}
                                                {expandedTeam === team.id && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        className="pt-3 border-t border-gray-200"
                                                    >
                                                        <h4 className="text-sm font-medium text-gray-700 mb-3">Team Members</h4>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            {getTeamMembers(team.id).map((member, idx) => (
                                                                <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                                                        <span className="text-blue-700 font-semibold text-sm">{member.avatar}</span>
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-gray-900">{member.name}</p>
                                                                        <p className="text-sm text-gray-600">{member.role}</p>
                                                                    </div>
                                                                    <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                                        <MessageSquare className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="mt-3 flex justify-end">
                                                            <button className="flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium">
                                                                <UserPlus className="w-4 h-4 mr-1" />
                                                                View All Members
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Empty State */}
                        {filteredTeams.length === 0 && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-12"
                            >
                                <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <Users className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No teams found</h3>
                                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                                    {searchTerm || selectedLocation !== 'all' || selectedStatus !== 'all'
                                        ? "No teams match your search criteria. Try adjusting your filters."
                                        : "No teams are currently available in the directory."
                                    }
                                </p>
                                {(searchTerm || selectedLocation !== 'all' || selectedStatus !== 'all') && (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setSelectedLocation('all');
                                            setSelectedStatus('all');
                                        }}
                                        className="btn-primary"
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </motion.div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Team Overview */}
                        <div className="card">
                            <div className="text-center mb-6">
                                <img
                                    src={teamImg}
                                    alt="Team Directory"
                                    className="w-full max-w-xs mx-auto mb-4 rounded-lg"
                                />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Team Directory</h3>
                                <p className="text-gray-600 text-sm">
                                    Connect with teams, collaborate on projects, and stay informed about team activities.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <Users className="w-5 h-5 text-blue-600" />
                                        <span className="font-medium text-gray-700">Your Teams</span>
                                    </div>
                                    <span className="font-medium text-gray-900">3 teams</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <MessageSquare className="w-5 h-5 text-green-600" />
                                        <span className="font-medium text-gray-700">Active Chats</span>
                                    </div>
                                    <span className="font-medium text-gray-900">5 conversations</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <Target className="w-5 h-5 text-purple-600" />
                                        <span className="font-medium text-gray-700">Shared Projects</span>
                                    </div>
                                    <span className="font-medium text-gray-900">8 projects</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                            <div className="space-y-2">
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <MessageSquare className="w-5 h-5" />
                                        <span className="font-medium">Start Team Chat</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Eye className="w-5 h-5" />
                                        <span className="font-medium">Browse Members</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Download className="w-5 h-5" />
                                        <span className="font-medium">Team Resources</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                            </div>
                        </div>

                        {/* Recent Team Activities */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
                            <div className="space-y-3">
                                {teams.slice(0, 3).map(team => (
                                    <div key={team.id} className="flex items-start space-x-3">
                                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                            <Users className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{team.name}</p>
                                            <p className="text-xs text-gray-500">
                                                {team.members} members • {team.projects} active projects
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Location Distribution */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Teams by Location</h3>
                            <div className="space-y-3">
                                {locations.filter(l => l !== "all").map(location => {
                                    const count = teams.filter(t => t.location === location).length;
                                    const percentage = Math.round((count / stats.totalTeams) * 100);

                                    return count > 0 ? (
                                        <div key={location} className="space-y-1">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-600">{location}</span>
                                                <span className="font-medium text-gray-900">{count} teams</span>
                                            </div>
                                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    ) : null;
                                }).filter(Boolean)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewTeams;